import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, Phone, Menu, Shield, Calendar } from 'lucide-react';

const GlobalNavigation = ({ 
  phone = '(916) 256-3468', 
  showMenu = true, 
  onMenuClick,
  language = 'en',
  className = ''
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    if (window.history?.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const goHome = () => {
    navigate('/');
  };

  const goToScheduleAppointment = () => {
    navigate('/appointment-scheduling-system');
  };

  const canGoBack = location?.pathname !== '/';

  const content = {
    en: {
      goBack: 'Go Back',
      home: 'Home',
      call: 'Call Now',
      menu: 'Menu',
      accessAccount: 'ACCESS YOUR BAIL ACCOUNT',
      scheduleAppointment: 'Schedule Appointment'
    },
    es: {
      goBack: 'Regresar', 
      home: 'Inicio',
      call: 'Llamar',
      menu: 'Menú',
      accessAccount: 'ACCEDER A SU CUENTA',
      scheduleAppointment: 'Programar Cita'
    }
  };

  const currentContent = content?.[language] || content?.en;

  return (
    <nav className={`bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-3 px-4 shadow-lg sticky top-0 z-50 ${className}`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          {/* Left side - Navigation Controls */}
          <div className="flex items-center space-x-4">
            {/* Go Back Button */}
            {canGoBack && (
              <button
                onClick={goBack}
                className="flex items-center bg-white/20 text-white px-3 py-2 rounded-full text-sm hover:bg-white/30 transition-colors"
                aria-label={currentContent?.goBack}
              >
                <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />
                <span className="hidden sm:inline">{currentContent?.goBack}</span>
              </button>
            )}
            
            {/* Go Home Button */}
            <button
              onClick={goHome}
              className="flex items-center bg-white/20 text-white px-3 py-2 rounded-full text-sm hover:bg-white/30 transition-colors"
              aria-label={currentContent?.home}
            >
              <Home className="w-4 h-4 mr-1" aria-hidden="true" />
              <span className="hidden sm:inline">{currentContent?.home}</span>
            </button>

            {/* Schedule Appointment Button */}
            <button
              onClick={goToScheduleAppointment}
              className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors shadow-md"
              aria-label={currentContent?.scheduleAppointment}
            >
              <Calendar className="w-5 h-5 mr-2 flex-shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline whitespace-nowrap">{currentContent?.scheduleAppointment}</span>
              <span className="sm:hidden">Schedule</span>
            </button>
            
            {/* Call Button */}
            <a 
              href={`tel:${phone?.replace(/[^\d]/g, '')}`}
              className="flex items-center bg-white text-red-600 px-4 py-2 rounded-full font-bold hover:bg-red-50 transition-colors text-sm"
            >
              <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
              <span className="hidden sm:inline">{currentContent?.call} </span>
              {phone}
            </a>
          </div>

          {/* Center - ACCESS YOUR BAIL ACCOUNT Button */}
          <div className="hidden lg:flex">
            <a 
              href="https://agents.simplybail.com/m/sailorsbailbonds43/office?code"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-6 py-2 rounded-xl font-black text-sm hover:from-yellow-300 hover:to-amber-400 transition-all transform hover:scale-105 shadow-lg border-2 border-yellow-300 hover:border-yellow-200 flex items-center"
            >
              <Shield className="w-4 h-4 mr-2 group-hover:animate-pulse" aria-hidden="true" />
              <span className="whitespace-nowrap">
                {currentContent?.accessAccount}
              </span>
            </a>
          </div>

          {/* Right side - Menu */}
          {showMenu && (
            <div className="flex items-center space-x-4">
              {/* Mobile ACCESS YOUR BAIL ACCOUNT Button */}
              <a 
                href="https://agents.simplybail.com/m/sailorsbailbonds43/office?code"
                target="_blank"
                rel="noopener noreferrer"
                className="lg:hidden group bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-3 py-2 rounded-lg font-bold text-xs hover:from-yellow-300 hover:to-amber-400 transition-all transform hover:scale-105 shadow-lg border border-yellow-300 hover:border-yellow-200 flex items-center"
              >
                <Shield className="w-3 h-3 mr-1 group-hover:animate-pulse" aria-hidden="true" />
                <span className="hidden md:inline">Current Clients - Access Your Bail Account</span>
                <span className="md:hidden">Current Clients - Access Account</span>
              </a>
              
              <button
                onClick={onMenuClick}
                className="bg-white/20 text-white px-3 py-2 rounded-full text-sm hover:bg-white/30 transition-colors flex items-center"
                aria-label={currentContent?.menu}
              >
                <Menu className="w-4 h-4 mr-1" aria-hidden="true" />
                {currentContent?.menu}
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default GlobalNavigation;